import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IntervalTypes } from '@guryou/core/build/intervals';
import {
  Block,
  CheckBox,
  DateRange,
  DateTimePicker,
  DefaultText,
  FlexRow,
  FullScreenSpinner,
  HiddenField,
  MediumText,
  NumberBox,
  SelectOption,
  SmallGrayText,
  SmallText,
  TextBox,
} from '@guryou/html-components';
import { DEFAULT_START_TIME, saveSession, stateIsLoading, useEffectOnError, useEffectOnSuccess } from '@guryou/redux';
import { addMinutes, addMonths, dateIsBetween, parseDate, printInputDate } from '@guryou/utils/build/dateTimeUtils';
import SelectableCubeGroup from 'onboard/components/StaffCube';
import WeekBuilder from 'onboard/components/WeekBuilder';
import WorkingDays from 'onboard/components/WorkingDays';
import CheckboxInfoContainer from 'onboard/containers/CheckboxInfoContainer';
import FormContainer from 'onboard/containers/FormContainer';
import InfoContainer from 'onboard/containers/InfoContainer';
import MainContainer from 'onboard/containers/MainContainer';
import PlayGuideVideoEmbed from 'onboard/containers/PlayGuideVideoEmbed';
import TitleContainer from 'onboard/containers/TitleContainer';
import { useOnboardContext } from 'onboard/context';
import { guideVideos } from 'onboard/GuideVideosUrls';

import StepButtons from '../StepButtons';

const CourseSessions = ({ gyClass }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [repeatDays, setRepeatDays] = useState(false);
  const [repeatIntervalType, setRepeatIntervalType] = useState('weekly');
  const [workingDaysError, setWorkingDaysError] = useState(false);
  const [addedDaysError, setAddedDaysError] = useState(false);
  const [staffError, setStaffError] = useState(false);

  const createSession = useSelector(state => state.sessions.create);

  const { resetCourseStep, serviceType, setActiveStep } = useOnboardContext();

  const [ls, setLs] = useState(() => {
    const initState = {
      startTime: DEFAULT_START_TIME,
    };
    initState.price = gyClass?.price;
    initState.duration = gyClass?.duration || 5;

    initState.endTime = addMinutes(initState.startTime, initState.duration);
    initState.startDate = gyClass?.startTime || new Date();
    const startPlusMonth = addMonths(gyClass?.startTime || new Date(), 1);
    const maxOneMonth = printInputDate(gyClass?.endTime) > printInputDate(startPlusMonth) ? startPlusMonth : gyClass?.endTime;
    initState.endDate = maxOneMonth || new Date();
    return initState;
  });

  useEffectOnSuccess(
    state => state.sessions.create,
    () => {
      resetCourseStep(3, serviceType);
    }
  );

  useEffectOnError(
    state => state.sessions.create,
    () => {
      setActiveStep(0);
    }
  );

  const handleSubmit = data => {
    if (data.repeatIntervalType === IntervalTypes.weekly && !data.addedDays) {
      setAddedDaysError(true);
      return;
    }

    if (data.repeatIntervalType === IntervalTypes.daily && !data.workingDays) {
      setWorkingDaysError(true);
      return;
    }

    if (!data.resourceId) {
      setStaffError(true);
      return;
    }

    dispatch(saveSession(data, true)); // isDeal = true
  };

  if (stateIsLoading(createSession)) {
    return <FullScreenSpinner />;
  }

  const preselected = gyClass?.resources && gyClass?.resources.length > 0 && [gyClass?.resources[0].id];

  return (
    <MainContainer>
      <FormContainer handleSubmit={handleSubmit}>
        <TitleContainer title={t('ONBOARD.createSessions')} />
        <Block lg={10} md={12} xs={12} margin={0} marginTop={0} flexWrap="wrap">
          <HiddenField name="classId" value={gyClass.id} />
          <HiddenField name="locationType" value="1" />
          {/* <SelectOption
            name="resourceId"
            xs={12}
            required
            fullWidth
            label={t('SUBSERVICES.staff')}
            // value={state?.data?.resourceId}
            margin="10px 0px 20px 0px"
            none={false}
            options={gyClass?.resources || []}
          /> */}
          <Block margin={0} marginTop={2}>
            <SelectableCubeGroup
              error={staffError}
              setError={setStaffError}
              multiple={false}
              resources={gyClass?.resources || []}
              values={preselected || []}
            />
          </Block>
          <FlexRow margin={0} padding={0}>
            <CheckBox
              xs={4}
              margin="10px 0px 10px 0px"
              label={t('SESSIONS.repeatSession')}
              color="default"
              onClick={e => setRepeatDays(e.target.checked)}
            />
          </FlexRow>

          {!repeatDays && (
            <>
              <FlexRow xs={12} margin={0} padding={0} justifyContent="space-between">
                <DateTimePicker
                  xs={2.6}
                  shouldDisableDate={date => !dateIsBetween(date, parseDate(gyClass.startTime), parseDate(gyClass.endTime))}
                  margin="10px 0px 20px 0px"
                  label={t('GENERAL.from')}
                  dateFieldName="startDate"
                  value={ls.startDate}
                  disablePast={false}
                />
                <NumberBox
                  xs={2.6}
                  fullWidth
                  margin="10px 0px 20px 0px"
                  name="duration"
                  value={ls.duration}
                  label={t('ONBOARD.duration')}
                  required
                  disabled
                  onChange={val => {
                    setLs(s => ({ ...s, duration: val, endTime: addMinutes(s.startTime, val) }));
                  }}
                />

                <DateTimePicker
                  xs={2.6}
                  margin="10px 0px 20px 0px"
                  label={t('GENERAL.from')}
                  timeFieldName="startTime"
                  value={ls.startTime}
                  onChange={val => {
                    setLs(s => ({ ...s, startTime: val, endTime: addMinutes(val, s.duration) }));
                  }}
                />
                <DateTimePicker
                  xs={2.6}
                  margin="10px 0px 20px 0px"
                  key={+ls.endTime}
                  label={t('GENERAL.to')}
                  timeFieldName="endTime"
                  value={ls.endTime}
                  disableField={true}
                />
              </FlexRow>
            </>
          )}
          {repeatDays && (
            <>
              <FlexRow xs={12} margin={0} padding={0} justifyContent="space-between">
                <Block xs={5.8} display="flex" margin="10px 0px 20px 0px" alignItems="end" justifyContent="space-between">
                  <SmallGrayText xs={5} textAlign="left">
                    {t('SESSIONS.repeatEvery')}
                  </SmallGrayText>
                  <TextBox
                    xs={2.5}
                    margin="none"
                    type="number"
                    disabled={printInputDate(ls.startDate) === printInputDate(ls.endDate)}
                    name="repeatInterval"
                    // onChange={val => setRepeatValue(+val)}
                    // value={state?.data?.repeatInterval || repeatValue || 1}
                    defaultValue="1"
                    minValue="1"
                  />
                </Block>
                <SelectOption
                  xs={5.8}
                  required
                  fullWidth
                  margin="10px 0px 20px 0px"
                  none={false}
                  name="repeatIntervalType"
                  value={repeatIntervalType}
                  options={[
                    { value: IntervalTypes.weekly, label: t('SESSIONS.weeks') },
                    { value: IntervalTypes.daily, label: t('SESSIONS.days') },
                  ]}
                  onChange={val => setRepeatIntervalType(val)}
                />
              </FlexRow>
              <FlexRow xs={12} margin={0} padding={0} justifyContent="space-between">
                <DateRange
                  inline={true}
                  xs={5.8}
                  wrapperMargin={0}
                  wrapperJustifyContent="space-between"
                  margin="10px 0px 20px 0px"
                  startDateFieldName="startDate"
                  endDateFieldName="endDate"
                  startValue={ls.startDate || new Date()}
                  endValue={ls.endDate || addMonths(new Date(), 1)}
                  startWithNullDate={true}
                  min={gyClass?.startTime}
                  max={gyClass?.endTime}
                  shouldDisableDate={date => !dateIsBetween(date, gyClass?.startTime, gyClass?.endTime)}
                  disablePast={false}
                />
              </FlexRow>
              <FlexRow xs={12} margin={0} padding={0} justifyContent="space-between">
                <NumberBox
                  xs={3.8}
                  fullWidth
                  margin="10px 0px 20px 0px"
                  name="duration"
                  value={ls.duration}
                  label={t('ONBOARD.duration')}
                  required
                  disabled
                  onChange={val => {
                    setLs(s => ({ ...s, duration: val, endTime: addMinutes(s.startTime, val) }));
                  }}
                />
                <DateTimePicker
                  xs={3.8}
                  margin="10px 0px 20px 0px"
                  label={t('GENERAL.from')}
                  timeFieldName="startTime"
                  value={ls.startTime}
                  onChange={val => {
                    setLs(s => ({ ...s, startTime: val, endTime: addMinutes(val, s.duration) }));
                  }}
                />
                <DateTimePicker
                  xs={3.8}
                  margin="10px 0px 20px 0px"
                  key={+ls.endTime}
                  label={t('GENERAL.to')}
                  timeFieldName="endTime"
                  value={ls.endTime}
                  disableField={true}
                />
              </FlexRow>
            </>
          )}

          {repeatDays && repeatIntervalType === 'daily' && (
            <Block margin={0} marginTop={4}>
              <WorkingDays />
              {workingDaysError && <SmallText color="red">{`* ${t('SESSIONS.selectDay')}`}</SmallText>}
            </Block>
          )}

          {repeatDays && repeatIntervalType === 'weekly' && (
            <Block margin={0} marginTop={0}>
              <WeekBuilder gyClass={gyClass} ls={ls} />
              {addedDaysError && <SmallText color="red">{`* ${t('SESSIONS.selectDay')}`}</SmallText>}
            </Block>
          )}

          <HiddenField value={ls.duration} name="duration"></HiddenField>
        </Block>
        <StepButtons finish={true} showPreviousButton={false} />
      </FormContainer>
      <InfoContainer>
        <MediumText>{t('ONBOARD_TEXT.courseSessionsTitle')}</MediumText>
        <DefaultText fontSize="1.2rem">{t('ONBOARD_TEXT.courseSessionsSubtitle')}</DefaultText>
        <Block margin={0} marginTop={10}>
          <DefaultText fontSize="1.2rem" fontWeight="800">
            {t('ONBOARD_TEXT.courseSessionsCheckboxTitle')}
          </DefaultText>
          <CheckboxInfoContainer text={t('ONBOARD_TEXT.courseSessionsCheckbox1Text')} />
          <CheckboxInfoContainer text={t('ONBOARD_TEXT.courseSessionsCheckbox2Text')} />
          <CheckboxInfoContainer text={t('ONBOARD_TEXT.courseSessionsCheckbox3Text')} />
        </Block>
        <PlayGuideVideoEmbed videoId={guideVideos[`${serviceType}Sessions`].videoId} modalTitle={guideVideos[`${serviceType}Sessions`].title} />
      </InfoContainer>
    </MainContainer>
  );
};

export default CourseSessions;
